<script setup lang="ts">
import {
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  ref,
  computed,
  watch,
  nextTick
} from 'vue'
import state from './state'
import { initAuth, getToken, clearTokens } from './state/auth'
import { reweight, stickyWeights, ORG_ID, AUTH_HOST } from './lib/utils'
import { renderEmbedCode } from './lib/embeds'
const AdminTools = defineAsyncComponent(
  () => import('./components/AdminTools.vue')
)
const StripeList = defineAsyncComponent(
  () => import('./components/StripeList.vue')
)

export interface Props {
  slug?: string
}

const props = withDefaults(defineProps<Props>(), {
  slug: ''
})

const loaded = ref(false)
const authenticated = ref(false)

const isPreviewing = computed(() => {
  return !!state.pagePreviewHTML.value
})

const editorStripes = computed(() =>
  reweight(
    state.page.value.stripes,
    stickyWeights(state.page.value.default_stripes)
  )
)

async function login() {
  const url = new URL(window.location.href)
  url.searchParams.delete('autologin')
  window.location.href = `${AUTH_HOST}/sso/authorize?org_id=${ORG_ID}&path=${url.pathname}`
}

function logout() {
  authenticated.value = false
  clearTokens()
  const redirect = window.location.href
  window.location.href = `${AUTH_HOST}/logout?redirect=${redirect}`
}

async function onLogout() {
  await logout()
}

function getCurrentSlug() {
  return window.location.pathname?.split('.')?.[0] || '/'
}

async function onAuthenticated() {
  try {
    // @todo This could trigger a loader while we retrieve current page data.
    await state.initFromSlug(props.slug || getCurrentSlug())
    checkForPasswordPrompt()
    toggleRenderedStripes(true)
    loaded.value = true
  } catch (e) {
    console.warn('Failed to load page data: ', e)
  }
}

function onWindowPointerDown() {
  state.activeTrayId.value = '-1'
}

function onWindowClick(e: Event) {
  const target: HTMLElement | null = e?.target as HTMLElement | null
  const clickEl =
    target && target.getAttribute('data-vssl-action')
      ? (target as HTMLElement)
      : (target?.closest('[data-vssl-action]') as HTMLElement)

  if (clickEl && clickEl.getAttribute('data-vssl-action')) {
    e.preventDefault()
    e.stopPropagation()

    switch (clickEl.getAttribute('data-vssl-action')) {
      case 'logout':
        logout()
        break
      case 'login':
        login()
        break
    }
  }
}

function onWindowKeydown(e: KeyboardEvent) {
  if (!authenticated.value && e.ctrlKey && ['l', 'L'].includes(e.key)) {
    login()
  }
}

// If we landed on a 401 page, that means the site is password protected, and we need to refresh the page so that we get the authenticated view -BM
function checkForPasswordPrompt() {
  const passwordInput = document.getElementById('password')
  if (passwordInput) {
    window.location.reload()
  }
}

function toggleRenderedStripes(hide: boolean) {
  const page = document.querySelector(
    '.vssl-page:not(.vssl-page--editor)'
  ) as HTMLElement

  // Only hide page if the current slug corresponds with a managed page.
  if (page && state.isManagedPage.value) {
    page.style.display = hide ? 'none' : 'initial'
  }
}

function getBufferEls() {
  return [
    document.getElementById('vssl-navigation-buffer'),
    document.getElementById('vssl-toolbar-buffer')
  ]
}

watch(authenticated, async (val) => {
  if (val) {
    onAuthenticated()
  }
})

watch(state.pagePreviewHTML, async (val) => {
  if (val) {
    await nextTick()
    const embedContainers = document.querySelectorAll('.vssl-stripe--embed')
    embedContainers.forEach((container) => {
      renderEmbedCode(container.innerHTML, container as HTMLElement)
    })
  }
})

onMounted(async () => {
  window.addEventListener('click', onWindowClick)
  window.addEventListener('pointerdown', onWindowPointerDown)
  window.addEventListener('keydown', onWindowKeydown)

  try {
    const url = new URL(window.location.href)
    /**
     * For uva-sites (and other implementations we support in the future with server-side auth), the code-to-token exchange is happening on the server auth middleware. We will still support the code being sent in a query param and letting the frontend editor do the exchange, for when we want to implement Vessel on sites without server-side auth (e.g. Cville Free Clinic) -BM
     */
    const code = url.searchParams.get('code') || ''
    const autologin = url.searchParams.get('autologin') === 'true'
    await initAuth(code)
    authenticated.value = !!getToken()

    // Remove code parameter if authenticated.
    if (authenticated.value && code) {
      url.searchParams.delete('code')
    }

    // Remove autologin parameter if authenticated.
    if (authenticated.value && autologin) {
      url.searchParams.delete('autologin')
    }
    if (authenticated.value && (code || autologin)) {
      window.history.replaceState({}, '', url)
    }

    if (!authenticated.value && autologin) {
      // If not authenticated, attempt an auto-login.
      login()
    }
  } catch (e) {
    console.warn('Failed to authenticate: ', e)
  } finally {
    loaded.value = !authenticated.value
  }

  const [navigationBufferEl, toolbarBufferEl] = getBufferEls()
  if (navigationBufferEl) {
    navigationBufferEl.remove()
    document.body.prepend(navigationBufferEl)
  }
  if (toolbarBufferEl) {
    toolbarBufferEl.remove()
    document.body.append(toolbarBufferEl)
  }
})

onUnmounted(() => {
  window.removeEventListener('click', onWindowClick)
  window.removeEventListener('pointerdown', onWindowPointerDown)
  window.removeEventListener('keydown', onWindowKeydown)
})
</script>

<template>
  <template v-if="loaded && authenticated">
    <Teleport to="body">
      <AdminTools
        id="vssl-admin-area"
        @logout="onLogout"
      />
    </Teleport>
    <Teleport to="[vssl-document]">
      <div
        :class="[
          'vssl-page',
          { 'vssl-page--editor': !isPreviewing },
          { 'vssl-page--preview': isPreviewing }
        ]"
        :data-id="state.page.value.id"
        :data-type="state.page.value.type"
        :hidden="!state.isManagedPage.value"
      >
        <div
          v-if="state.pagePreviewHTML.value"
          class="vssl-stripes"
          v-html="state.pagePreviewHTML.value"
        />
        <StripeList
          v-else
          id="vssl-page-area"
          :class="[
            'vssl-stripes',
            {
              'vssl-page-area--loading-preview': state.loadingPagePreview.value
            }
          ]"
          :stripes="editorStripes"
        />
      </div>
    </Teleport>
  </template>

  <div
    v-show="authenticated"
    id="vssl-navigation-buffer"
  />
  <div
    v-show="authenticated"
    id="vssl-toolbar-buffer"
  />
</template>

<style>
#vssl-navigation-buffer {
  height: 60px;
}

#vssl-toolbar-buffer {
  height: 64px;
}

.vssl-page-area--loading-preview {
  /* animation: blurToWhite 1s ease-in-out forwards; */
  opacity: 0.333;
  pointer-events: none;
  transition: opacity 0.2s var(--vssl-ease-in-out-fast);
}

/* @keyframes blurToWhite {
  0% {
    background-color: transparent;
    filter: blur(0);
  }

  100% {
    background-color: white;
    filter: blur(16px);
  }
} */
</style>
